/*
* Palo Alto Theme
*
* Use this file to add custom Javascript to Palo Alto.  Keeping your custom
* Javascript in this fill will make it easier to update Palo Alto. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/


(function() {
  // Add custom code below this line

  /* Client customization: product gallery sizing + media normalization. */
  const GALLERY_SELECTOR =
    '.product-single__wrapper--carousel .product-gallery';

  const SLIDER_SELECTOR =
    '[data-product-single-media-slider]';

  const SLIDE_SELECTOR =
    '[data-product-single-media-wrapper]';

  const THUMBS_SELECTOR =
    '[data-product-single-media-thumbs]';

  const IMAGE_MEDIA_SELECTOR =
    '.product-gallery__media--image';


  const number = (value) => {
    const parsed = parseFloat(value);

    return Number.isFinite(parsed)
      ? parsed
      : 0;
  };


  const cssPixels = (styles, property) => {
    return Math.max(
      0,
      number(styles.getPropertyValue(property))
    );
  };


  /*
   * Detect the underlying Shopify image format.
   *
   * Query strings such as:
   *
   *   image.png?v=12345
   *
   * are supported.
   */
  const getImageFormat = (img) => {
    if (!img) return 'other';

    const source =
      img.currentSrc ||
      img.getAttribute('src') ||
      '';

    if (/\.png(?:$|[?#])/i.test(source)) {
      return 'png';
    }

    if (/\.jpe?g(?:$|[?#])/i.test(source)) {
      return 'jpg';
    }

    return 'other';
  };


  /*
   * Mark the media wrapper so CSS can apply format-specific behavior.
   */
  const classifyImageMedia = (slide) => {
    const media =
      slide.querySelector(IMAGE_MEDIA_SELECTOR);

    if (!media) return null;

    const img =
      media.querySelector('img');

    const format =
      getImageFormat(img);

    media.dataset.galleryImageFormat =
      format;

    slide.dataset.galleryImageFormat =
      format;

    return {
      media,
      img,
      format
    };
  };


  /*
   * Only PNGs receive compatibility padding.
   *
   * JPG/JPEG and anything unclassified explicitly resolve to zero.
   */
  const getImagePadding = (
    media,
    format
  ) => {

    if (format !== 'png') {
      return {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      };
    }

    const styles =
      getComputedStyle(media);

    return {
      top: cssPixels(
        styles,
        '--product-gallery-png-padding-top'
      ),

      right: cssPixels(
        styles,
        '--product-gallery-png-padding-right'
      ),

      bottom: cssPixels(
        styles,
        '--product-gallery-png-padding-bottom'
      ),

      left: cssPixels(
        styles,
        '--product-gallery-png-padding-left'
      )
    };
  };


  /*
   * Determine how tall this IMAGE wants to be when width is the
   * controlling dimension.
   *
   * Shopify's data-aspectratio value is:
   *
   *     width / height
   *
   * PNG padding participates in the calculation.
   */
  const getRequiredImageHeight = (
    slide,
    sliderWidth
  ) => {

    const image =
      classifyImageMedia(slide);

    /*
     * Videos/models return zero here and therefore never contribute
     * to maximum gallery height.
     */
    if (!image) {
      return 0;
    }

    const aspectRatio =
      number(
        slide.getAttribute(
          'data-aspectratio'
        )
      );

    if (aspectRatio <= 0) {
      return 0;
    }

    const padding =
      getImagePadding(
        image.media,
        image.format
      );

    /*
     * Horizontal padding reduces the width available to the actual
     * PNG image content.
     */
    const availableWidth =
      Math.max(
        1,
        sliderWidth -
          padding.left -
          padding.right
      );

    /*
     * Width-first image height:
     *
     * content height =
     *     available content width / aspect ratio
     *
     * then restore the vertical compatibility padding.
     */
    return (
      padding.top +
      (availableWidth / aspectRatio) +
      padding.bottom
    );
  };


  /*
   * Gallery height is simply the tallest IMAGE in the set.
   *
   * There is no longer a square minimum.
   *
   * Videos and models do not participate.
   */
  const getGalleryHeight = (
    slides,
    sliderWidth
  ) => {

    let tallestImage = 0;

    slides.forEach((slide) => {
      tallestImage =
        Math.max(
          tallestImage,
          getRequiredImageHeight(
            slide,
            sliderWidth
          )
        );
    });

    /*
     * Safety fallback:
     *
     * A product containing only video/model media still needs a usable
     * canvas. In that unusual case only, use a square viewport.
     */
    return Math.ceil(
      tallestImage ||
      sliderWidth
    );
  };


  /*
   * Ask an already-created Palo Alto Flickity instance to remeasure.
   *
   * This is additive; Flickity ownership remains with the theme.
   */
  const resizeFlickity = (element) => {
    if (
      !element ||
      !window.theme ||
      !window.theme.Flickity
    ) {
      return;
    }

    const instance =
      window.theme.Flickity.data(
        element
      );

    if (
      instance &&
      typeof instance.resize === 'function'
    ) {
      instance.resize();
    }
  };


  const sizeGallery = (gallery) => {

    const slider =
      gallery.querySelector(
        SLIDER_SELECTOR
      );

    if (!slider) {
      return;
    }


    const slides =
      Array.from(
        slider.querySelectorAll(
          SLIDE_SELECTOR
        )
      );

    if (!slides.length) {
      return;
    }


    /*
     * Measure the actual main-gallery column.
     *
     * On desktop this already accounts for the width consumed by the
     * vertical thumbnail rail.
     */
    const sliderWidth =
      slider
        .getBoundingClientRect()
        .width;

    if (!sliderWidth) {
      return;
    }


    const galleryHeight =
      getGalleryHeight(
        slides,
        sliderWidth
      );


    /*
     * Mark the gallery as ready BEFORE CSS starts overriding Palo Alto's
     * built-in aspect-ratio spacers.
     */
    gallery.dataset.gallerySized =
      'true';

    slider.dataset.gallerySized =
      'true';


    gallery.style.setProperty(
      '--product-gallery-media-height',
      `${galleryHeight}px`
    );

    slider.style.setProperty(
      '--product-gallery-media-height',
      `${galleryHeight}px`
    );


    /*
     * Give every slide a real explicit pixel height.
     *
     * This is particularly important for products with ONE image,
     * where Flickity may not establish/manage a normal viewport.
     */
    slides.forEach((slide) => {

      slide.style.height =
        `${galleryHeight}px`;


      const media =
        slide.querySelector(
          '.product-gallery__media'
        );

      if (media) {
        media.style.height =
          `${galleryHeight}px`;
      }


      /*
       * IMAGE media was already classified while calculating the gallery.
       *
       * Everything else is video/model media and should be HEIGHT-FIRST.
       */
      if (
        !slide.querySelector(
          IMAGE_MEDIA_SELECTOR
        )
      ) {

        const mediaRatio =
          number(
            slide.getAttribute(
              'data-aspectratio'
            )
          ) ||
          (16 / 9);


        /*
        * Native <video> elements can use their intrinsic aspect ratio.
        * External iframe media cannot, so expose Shopify's media ratio to CSS.
        */
        slide.style.setProperty(
          '--product-gallery-media-aspect-ratio',
          mediaRatio
        );
      }
    });


    /*
     * Preserve the shared-height thumbnail treatment.
     *
     * Thumbnail height comes from the tallest IMAGE ratio in the set.
     * Videos still do not participate.
     */
    const thumbs =
      gallery.querySelector(
        THUMBS_SELECTOR
      );


    if (thumbs) {

      const thumbCells =
        Array.from(
          thumbs.querySelectorAll(
            '.product-single__thumbnail'
          )
        );


      let tallestScale = 1;


      slides.forEach((slide) => {

        if (
          !slide.querySelector(
            IMAGE_MEDIA_SELECTOR
          )
        ) {
          return;
        }


        const ratio =
          number(
            slide.getAttribute(
              'data-aspectratio'
            )
          );


        if (ratio > 0) {
          tallestScale =
            Math.max(
              tallestScale,
              1 / ratio
            );
        }
      });


      thumbCells.forEach(
        (cell, index) => {

          const link =
            cell.querySelector(
              '.product-single__thumbnail-link'
            );


          const thumbWidth =
            (link || cell)
              .getBoundingClientRect()
              .width;


          if (!thumbWidth) {
            return;
          }


          /*
           * Carry the matching main-image format down to the thumbnail.
           */
          const matchingSlide =
            slides[index];


          const format =
            matchingSlide
              ?.dataset
              .galleryImageFormat ||
            'other';


          cell.dataset.galleryImageFormat =
            format;


          cell.style.setProperty(
            '--product-gallery-thumb-height',
            `${Math.ceil(
              thumbWidth *
              tallestScale
            )}px`
          );
        }
      );
    }


    /*
     * Let our geometry settle before asking Flickity to remeasure.
     */
    requestAnimationFrame(() => {

      resizeFlickity(slider);

      resizeFlickity(thumbs);

    });
  };


  const sizeAll = () => {

    document
      .querySelectorAll(
        GALLERY_SELECTOR
      )
      .forEach(
        sizeGallery
      );

  };


  let resizeFrame = null;


  const queueSizeAll = () => {

    if (resizeFrame) {
      cancelAnimationFrame(
        resizeFrame
      );
    }


    resizeFrame =
      requestAnimationFrame(() => {

        resizeFrame = null;

        sizeAll();

      });
  };


  const initProductGallerySizing =
    () => {

      queueSizeAll();


      /*
       * Lazy-loaded media may settle after DOMContentLoaded.
       */
      if (
        document.readyState ===
        'complete'
      ) {

        queueSizeAll();

      } else {

        window.addEventListener(
          'load',
          queueSizeAll,
          {
            once: true
          }
        );

      }
    };


  /*
   * Works whether custom.js is loaded before or after DOM ready.
   */
  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initProductGallerySizing,
      {
        once: true
      }
    );

  } else {

    initProductGallerySizing();

  }


  /*
   * Shopify Theme Editor section reload.
   */
  document.addEventListener(
    'shopify:section:load',
    queueSizeAll
  );


  /*
   * Palo Alto's own width-change event.
   */
  document.addEventListener(
    'theme:resize:width',
    queueSizeAll
  );


  /*
   * Browser fallback.
   */
  window.addEventListener(
    'resize',
    queueSizeAll,
    {
      passive: true
    }
  );


  /*
   * Lazy image source changes or Shopify media replacement can alter
   * formats / available dimensions after initial load.
   */
  const observer =
    new MutationObserver(
      (mutations) => {

        const galleryChanged =
          mutations.some(
            (mutation) => {

              const target =
                mutation.target;


              return (
                target instanceof Element &&
                target.closest(
                  GALLERY_SELECTOR
                )
              );
            }
          );


        if (galleryChanged) {
          queueSizeAll();
        }
      }
    );


  observer.observe(
    document.documentElement,
    {
      subtree: true,
      childList: true,
      attributes: true,

      attributeFilter: [
        'src',
        'srcset',
        'data-aspectratio'
      ]
    }
  );






  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();